// Keeps one pull request's review marks and GitHub's per-file "Viewed"
// checkbox in step, by the rules of logic/viewedSync.ts (docs/design.md,
// "GitHub's Viewed checkbox").
//
// What it needs of review marks is three things, behind `FileReviewPort`:
// whether every change of a file is reviewed at the head, the file's diff
// there, and marking (or unmarking) a whole file at that head. GitHub's
// side comes in as `remote` (the states read with the pull request, a page
// at a time) and goes out through `write` (the markFileAsViewed /
// unmarkFileAsViewed mutations).
//
// Writes wait for a quiet period per file (`debounceMs`), so marking a file
// change by change sends one write, and one that is undone in time sends
// none. They go one at a time, at least `spacingMs` apart. A failed write
// is tried again when `retryAt` says (with backoff); one it gives up on is
// shown as an error and not tried again until the file's review state
// changes, or `retry`. A failed import is tried again the same way.
//
// GitHub's mutation names no head, so a write is for the head it was
// decided at: it is dropped when the head moves before it is sent, `write`
// refuses it (HeadMovedError) when the head moved since, and when GitHub
// reports another head after a mark landed, the file is unmarked at once
// there (a correction, retried like any write). A mark's baseline is
// stored before it is sent, flagged uncertain, with the diff it was
// decided for; the flag is cleared when GitHub confirms the head it landed
// on, and kept when the outcome is unknown (the answer was lost, or named
// no head), for the next read to settle (logic/viewedSync.ts).
//
// When GitHub refuses the mutation for the pull request (`refusal`), the
// sync pauses: nothing more is written or imported, no state is shown per
// file, and `paused` says why.
//
// The baselines (what both sides last agreed on, per file) are kept in
// `store`, so a reload still tells an unmark here from a change on GitHub.
import { baselineAt, decide, type Baseline, type ViewedState } from '../logic/viewedSync';

/** Review marks, as the sync sees them: one pull request's files at one head. */
export interface FileReviewPort {
  /** The pull request head the files are judged at. */
  readonly head: string;
  /**
   * Every change of `path` is reviewed at `head`; null while unknown, and
   * for a file with no change block (only a rename or a mode change): such
   * a file is never written to GitHub, nor imported.
   */
  fullyReviewed(path: string): boolean | null;
  /** An identity of the file's diff at `head` (its base and head blobs); null when unknown. */
  diff(path: string): string | null;
  /** Mark (or unmark) every change of `path`, through the normal mark write. Refuses (throws) unless the marks are still judged at `head`. */
  setFile(path: string, reviewed: boolean, head: string): Promise<void>;
}

/** GitHub's viewed states for the viewer (all files, or a page of them), read at `head` by a request that started at `at` (epoch ms). */
export interface RemoteViewed {
  head: string;
  at: number;
  states: Record<string, ViewedState>;
}

/** Where a write landed: the pull request head GitHub reported with its answer; null when unknown. */
export interface WriteResult {
  head: string | null;
}

/** A write refused before it was sent: the pull request's head is no longer the one it was decided at. */
export class HeadMovedError extends Error {
  constructor(
    public expected: string,
    public actual: string,
  ) {
    super(`the pull request moved from ${expected.slice(0, 7)} to ${actual.slice(0, 7)}`);
  }
}

/** Where one pull request's baselines are kept. Either method may fail; the sync carries on without. */
export interface BaselineStore {
  read(): Promise<Record<string, Baseline> | undefined>;
  write(baselines: Record<string, Baseline>): Promise<void>;
}

export interface ViewedSyncOptions {
  /**
   * Set the viewer's checkbox for `path` on GitHub, for the pull request at
   * `head`. A mark (viewed) throws HeadMovedError when the head is not
   * `head`; an unmark is sent whatever the head.
   */
  write: (path: string, viewed: boolean, head: string) => Promise<WriteResult>;
  store: BaselineStore;
  /** Quiet period before a file's write is sent (ms). */
  debounceMs?: number;
  /** Least time between two writes (ms), so a burst (marking every file) is paced. */
  spacingMs?: number;
  now?: () => number;
  /** When to try a failed write again (epoch ms), after `failures` failed tries of it (1 or more); null to give up. */
  retryAt: (error: unknown, failures: number) => number | null;
  /** When to try a failed import again; `retryAt` when not given. */
  importRetryAt?: (error: unknown, failures: number) => number | null;
  /** Why GitHub refuses the mutation for the whole pull request (a permission), or null for a failure of one write. */
  refusal?: (error: unknown) => string | null;
  /** The sync paused, for `reason`. */
  onPause?: (reason: string) => void;
  /** A write found the pull request at another head than the one shown. */
  onHeadMoved?: () => void;
}

/** One file's sync, as shown. */
export interface FileSyncState {
  /** GitHub's checkbox, as it is or is about to be; null while unknown. */
  github: ViewedState | null;
  /** `sending`: a write (or a mark from GitHub) is waiting or in flight. */
  phase: 'idle' | 'sending' | 'error';
  error: string | null;
}

interface Pending {
  viewed: boolean;
  /** The head the write is for. */
  head: string;
  /** What both sides agree on once it lands; null for a correction. */
  baseline: Baseline | null;
  timer: ReturnType<typeof setTimeout> | null;
  failures: number;
  sending: boolean;
}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export class ViewedSync {
  /** Off: nothing is read into marks or written to GitHub, and no state is shown. */
  enabled = $state(true);
  /** Why GitHub refused the mutation for this pull request; while set, nothing is synced. */
  paused: string | null = $state(null);
  private shown: Record<string, FileSyncState> = $state({});
  private baselines: Record<string, Baseline> = {};
  private loaded = false;
  private port: FileReviewPort | null = null;
  private local: Record<string, boolean | null> = {};
  /** GitHub's states at `remoteHead`, each with when its read started. */
  private remoteHead: string | null = null;
  private remoteFiles: Record<string, { state: ViewedState; at: number }> = {};
  private pending = new Map<string, Pending>();
  /** Writes given up on, by file: not tried again while the file's review state is what it was. */
  private failed = new Map<string, { viewed: boolean; local: boolean | null; error: string }>();
  /** Imports given up on, by file: not tried again until GitHub is read again. */
  private importFailed = new Map<string, string>();
  /** Imports waiting to be tried again, by file. */
  private importRetry = new Map<string, { failures: number; timer: ReturnType<typeof setTimeout> | null }>();
  private importing = new Set<string>();
  /** Files whose write is due, in order; sent one at a time. */
  private due: string[] = [];
  private lastSent = -Infinity;
  /** Imports run one at a time (each may load a file's diff). */
  private imports: Promise<void> = Promise.resolve();
  private disposed = false;
  private readonly debounceMs: number;
  private readonly spacingMs: number;
  private readonly now: () => number;

  constructor(private opts: ViewedSyncOptions) {
    this.debounceMs = opts.debounceMs ?? 1000;
    this.spacingMs = opts.spacingMs ?? 0;
    this.now = opts.now ?? Date.now;
    this.read = opts.store
      .read()
      .catch(() => undefined)
      .then((b) => {
        this.baselines = { ...(b ?? {}), ...this.baselines };
        this.loaded = true;
        this.reconcileAll();
      });
  }

  /** The stored baselines' read, done once they are in. */
  private readonly read: Promise<void>;

  /**
   * Resolves once the stored baselines are read and nothing is under way:
   * no import runs and no write is being sent. A write still waiting out
   * its quiet period or a retry is not under way (its timer starts it).
   * For tests, which must know the sync has finished deciding.
   */
  async settled(): Promise<void> {
    await this.read;
    for (;;) {
      const imports = this.imports;
      const running = this.running;
      await imports;
      await running;
      if (imports === this.imports && this.running === null) return;
    }
  }

  /** One file's sync state; null while off, paused or unknown. */
  stateOf(path: string): FileSyncState | null {
    return this.enabled && !this.paused ? (this.shown[path] ?? null) : null;
  }

  /** Files whose sync failed. */
  get errors(): number {
    return this.enabled && !this.paused ? Object.values(this.shown).filter((s) => s.phase === 'error').length : 0;
  }

  setEnabled(on: boolean) {
    if (on === this.enabled) return;
    this.enabled = on;
    if (!on) this.stopAll();
    this.reconcileAll();
  }

  /** Stop syncing this pull request, for `reason` (GitHub refuses the mutation). */
  pause(reason: string) {
    if (this.paused) return;
    this.paused = reason;
    this.stopAll();
    this.opts.onPause?.(reason);
    this.reconcileAll();
  }

  /** GitHub's states, read with the pull request (or a further page of them); at another head than before, they replace what was read. */
  remote(r: RemoteViewed | null) {
    if (!r) {
      this.remoteHead = null;
      this.remoteFiles = {};
    } else {
      if (r.head !== this.remoteHead) {
        this.remoteHead = r.head;
        this.remoteFiles = {};
        this.dropStale(r.head);
      }
      for (const [path, state] of Object.entries(r.states)) {
        const was = this.remoteFiles[path];
        if (!was || was.at <= r.at) this.remoteFiles[path] = { state, at: r.at };
        this.importFailed.delete(path);
      }
    }
    this.reconcileAll();
  }

  /** The files' review state here (`port.fullyReviewed` of each), and the port to mark through; null when none is judged at the head. */
  observe(port: FileReviewPort | null, local: Record<string, boolean | null>) {
    this.port = port;
    const before = this.local;
    this.local = local;
    for (const [path, f] of this.failed) if (f.local !== local[path]) this.failed.delete(path);
    this.dropStale(port?.head ?? null);
    if (!port) return;
    for (const path of new Set([...Object.keys(local), ...Object.keys(before)])) if (local[path] !== before[path] || !this.shown[path]) this.reconcile(path);
  }

  /** Try a failed file again. */
  retry(path: string) {
    this.failed.delete(path);
    this.importFailed.delete(path);
    this.reconcile(path);
  }

  /** Try every failed file again. */
  retryAll() {
    for (const path of new Set([...this.failed.keys(), ...this.importFailed.keys()])) this.retry(path);
  }

  /** Send every waiting write now. */
  async flush(): Promise<void> {
    for (const [path, p] of this.pending) if (!p.sending && p.timer) this.makeDue(path, p);
    await this.pump();
  }

  /** Stop: waiting writes are sent once (each still checked against the head), nothing more is decided. */
  dispose() {
    void this.flush().finally(() => (this.disposed = true));
    for (const r of this.importRetry.values()) if (r.timer) clearTimeout(r.timer);
    this.importRetry.clear();
  }

  private stopAll() {
    for (const [path, p] of this.pending) if (!p.sending) this.drop(path);
    this.due = [];
    this.failed.clear();
    this.importFailed.clear();
    for (const r of this.importRetry.values()) if (r.timer) clearTimeout(r.timer);
    this.importRetry.clear();
  }

  /** Drop the writes not sent yet that were decided at another head than `head` (corrections excepted). */
  private dropStale(head: string | null) {
    for (const [path, p] of this.pending)
      if (!p.sending && p.baseline && p.head !== head) {
        this.drop(path);
        this.show(path);
      }
  }

  private reconcileAll() {
    const paths = new Set([...Object.keys(this.local), ...Object.keys(this.remoteFiles), ...Object.keys(this.shown)]);
    for (const p of paths) this.reconcile(p);
  }

  private reconcile(path: string) {
    if (this.disposed || !this.loaded) return;
    if (!this.enabled || this.paused) {
      this.show(path, null);
      return;
    }
    const port = this.port;
    if (!port || this.remoteHead !== port.head) return this.show(path);
    const pending = this.pending.get(path);
    // A write in flight, a correction, or an import decides the file when it is done.
    if (pending?.sending || (pending && !pending.baseline) || this.importing.has(path) || this.importRetry.get(path)?.timer) return this.show(path);
    const r = this.remoteFiles[path];
    const d = decide(this.baselines[path], {
      head: port.head,
      local: this.local[path] ?? null,
      remote: r?.state,
      remoteAt: r?.at ?? 0,
      writing: !!pending,
      diff: port.diff(path),
    });
    switch (d.kind) {
      case 'none':
        // The change a waiting write was for was undone in time.
        if (pending) this.drop(path);
        break;
      case 'settle':
        if (pending) this.drop(path);
        this.record(path, d.baseline);
        break;
      case 'push': {
        const f = this.failed.get(path);
        if (f && f.viewed === d.viewed) break;
        if (pending?.viewed === d.viewed) break;
        if (pending) this.drop(path);
        this.enqueue(path, { viewed: d.viewed, head: port.head, baseline: d.baseline, timer: null, failures: 0, sending: false }, this.debounceMs);
        break;
      }
      case 'import':
        if (this.importFailed.has(path)) break;
        this.startImport(path, port, d.baseline);
        break;
    }
    this.show(path);
  }

  private enqueue(path: string, p: Pending, delay: number) {
    this.pending.set(path, p);
    if (delay > 0) p.timer = setTimeout(() => this.makeDue(path, p), delay);
    else this.makeDue(path, p);
  }

  private startImport(path: string, port: FileReviewPort, baseline: Baseline) {
    this.importing.add(path);
    this.imports = this.imports.then(async () => {
      try {
        if (!this.enabled || this.paused || this.disposed) return;
        // Decided at a head the marks are no longer judged at: decided again there.
        if (this.port?.head !== baseline.head) return;
        await port.setFile(path, true, baseline.head);
        this.importRetry.delete(path);
        this.record(path, { ...baseline, local: port.fullyReviewed(path) ?? true });
      } catch (e) {
        const r = this.importRetry.get(path) ?? { failures: 0, timer: null };
        r.failures++;
        const at = this.disposed || this.paused ? null : (this.opts.importRetryAt ?? this.opts.retryAt)(e, r.failures);
        if (at === null) {
          this.importRetry.delete(path);
          this.importFailed.set(path, `Couldn't mark this file reviewed from GitHub: ${message(e)}`);
        } else {
          r.timer = setTimeout(
            () => {
              r.timer = null;
              this.reconcile(path);
            },
            Math.max(0, at - this.now()),
          );
          this.importRetry.set(path, r);
        }
      } finally {
        this.importing.delete(path);
        this.show(path);
      }
    });
  }

  private drop(path: string) {
    const p = this.pending.get(path);
    if (p?.timer) clearTimeout(p.timer);
    this.pending.delete(path);
    this.due = this.due.filter((x) => x !== path);
  }

  private makeDue(path: string, p: Pending) {
    if (p.timer) clearTimeout(p.timer);
    p.timer = null;
    if (this.pending.get(path) !== p) return;
    if (!this.due.includes(path)) this.due.push(path);
    void this.pump();
  }

  private running: Promise<void> | null = null;
  private pump(): Promise<void> {
    if (this.running) return this.running;
    this.running = (async () => {
      while (this.due.length) {
        const wait = this.lastSent + this.spacingMs - this.now();
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
        const path = this.due.shift();
        const p = path === undefined ? undefined : this.pending.get(path);
        if (path !== undefined && p) await this.send(path, p);
      }
    })().finally(() => (this.running = null));
    return this.running;
  }

  private async send(path: string, p: Pending) {
    if (this.paused) return this.drop(path);
    // Decided at a head the marks are no longer judged at.
    if (p.baseline && this.port && this.port.head !== p.head) {
      this.drop(path);
      return this.show(path);
    }
    p.sending = true;
    this.lastSent = this.now();
    this.show(path);
    if (p.baseline && p.viewed) this.recordBefore(path, p.head, p.baseline);
    try {
      const res = await this.opts.write(path, p.viewed, p.head);
      p.sending = false;
      this.pending.delete(path);
      this.failed.delete(path);
      if (!p.baseline) {
        // A correction landed: reads from before it are stale.
        const b = this.baselines[path];
        if (b) this.record(path, { ...b, at: this.now() });
      } else if (p.viewed && res.head === null) {
        // Landed, at a head not known: no correction (it may have ticked this one, rightly); the next read settles it.
        const b = this.baselines[path];
        if (b) this.record(path, { ...b, at: this.now(), uncertain: true });
      } else {
        const { uncertain: _, ...landed } = p.baseline;
        this.record(path, { ...landed, at: this.now() });
        if (p.viewed && res.head && res.head !== p.head) this.correct(path, res.head);
      }
      this.reconcile(path);
    } catch (e) {
      p.sending = false;
      if (e instanceof HeadMovedError) {
        this.pending.delete(path);
        // An earlier try may have landed, unanswered, at the new head.
        if (p.viewed && p.failures > 0) this.correct(path, e.actual);
        else this.opts.onHeadMoved?.();
        return this.show(path);
      }
      p.failures++;
      const reason = this.opts.refusal?.(e) ?? null;
      if (reason) {
        this.pending.delete(path);
        return this.pause(reason);
      }
      const at = this.disposed ? null : this.opts.retryAt(e, p.failures);
      if (at === null) {
        this.pending.delete(path);
        this.failed.set(path, { viewed: p.viewed, local: this.local[path] ?? null, error: message(e) });
      } else p.timer = setTimeout(() => this.makeDue(path, p), Math.max(0, at - this.now()));
      this.show(path);
    }
  }

  /** A mark decided at one head landed (or may have) at `head`: unmark it there now. */
  private correct(path: string, head: string) {
    this.opts.onHeadMoved?.();
    this.drop(path);
    this.enqueue(path, { viewed: false, head, baseline: null, timer: null, failures: 0, sending: false }, 0);
  }

  /** Before a mark is sent, store the agreement at its head with the diff it was decided for, in doubt until GitHub answers with a head. */
  private recordBefore(path: string, head: string, decided: Baseline) {
    const b = this.baselines[path];
    this.record(path, { ...baselineAt(b, head), diff: decided.diff, uncertain: true });
  }

  private record(path: string, b: Baseline) {
    this.baselines = { ...this.baselines, [path]: b };
    void this.opts.store.write(this.baselines).catch(() => {});
  }

  /** Update what `path` shows; `null` clears it. */
  private show(path: string, clear?: null): void {
    if (clear === null) {
      if (path in this.shown) {
        const { [path]: _, ...rest } = this.shown;
        this.shown = rest;
      }
      return;
    }
    const next = this.computeState(path);
    const now = this.shown[path];
    if (now && next && now.github === next.github && now.phase === next.phase && now.error === next.error) return;
    if (!next) return this.show(path, null);
    this.shown = { ...this.shown, [path]: next };
  }

  private computeState(path: string): FileSyncState | null {
    if (!this.enabled || this.paused) return null;
    const p = this.pending.get(path);
    const f = this.failed.get(path);
    const importError = this.importFailed.get(path);
    const importingNow = this.importing.has(path) || !!this.importRetry.get(path)?.timer;
    const head = this.port?.head ?? this.remoteHead;
    const r = this.remoteHead === head ? this.remoteFiles[path] : undefined;
    let github: ViewedState | null = null;
    if (p) github = p.viewed ? 'VIEWED' : 'UNVIEWED';
    else if (importingNow) github = 'VIEWED';
    else if (r && head) {
      const b = this.baselines[path];
      github = b && b.head === head && r.at < b.at ? b.remote : r.state;
    }
    if (github === null && !f && !importError) return null;
    if (f) return { github, phase: 'error', error: f.error };
    if (importError) return { github, phase: 'error', error: importError };
    return { github, phase: p || importingNow ? 'sending' : 'idle', error: null };
  }
}
